import apiClient from './api';
import type {
  RegisterAnimalRow, RegisterBreedingRow, RegisterCalvingRow, RegisterDashboard, RegisterMilkSheet, RegisterSettings,
  RegisterSheet, RegisterVetRow,
  AccountStatus, BillingMode, BreedingAlerts, CustomerAccountSummary, CustomerPayment,
  ActivityLog, Animal, AnimalCreate, AnimalEvent, AnimalIdentification, AnimalLifecycle, AnimalMovement, BulkImportColumnHelp, BulkImportResult,
  AnimalOffspringLink, AnimalPurchase, AnimalType, AnimalUpdate, Asset, AssetCreate,
  AssetType, Attachment, Breed, BreedingKPI, BreedingRecord, CalfRecord, CalvingRecord,
  CoconutBulkImportResult, CoconutHarvest, CoconutTree, CoconutTreeCreate, CoconutYieldReport, CowProfile, Customer,
  CustomerMilkSalesReport, DairyCollectionHub, DashboardKPIs,
  Deworming, DiseaseOutbreak, EggProduction, Expense, ExpenseApprovalStatus,
  ExpenseCategory, FarmContact, FarmMaster, FeedConsumption, FeedItem, GrowthRecord,
  HealthRecord, HeatDetection, InventoryItem, InventoryLocation, InventoryLot,
  InventoryTransaction, InventoryTransactionType, Lactation, LambingKiddingRecord,
  ListQueryParams, Location, LoginRequest, MedicineMaster, MilkChartResponse, MilkCollectionApprovalStatus, MilkDailySummary,
  MilkProductionReport, MilkSale, MilkSaleConfirmRequest, MilkSession, MilkSessionType, MilkShift,
  Notification, NotificationRule, Paginated, PaymentStatus, PendingSubscriptionConfirmation, PermissionRead, PoultryBatch,
  PoultryBatchReconciliation, PoultryFeedRecord, PoultryKPI, PoultryMortality,
  PregnancyRecord, PublicEnquiry, Purchase, PurchaseCreate, PurchaseOrder, PurchaseOrderCreate,
  PurchaseOrderStatus, Role, Sale, SaleCreate, SemenInventory, SemenMaster, ShedMaster,
  Supplier, SubscriptionDayChange, SubscriptionPlan, SystemSetting, Task, TaskAssignment, TaskOccurrence, TokenResponse, Treatment,
  TreatmentMedicine, User, UserRoleAssignment, UserSession, UtilityMeter, UtilityReading,
  VaccinationSchedule, VaccinationDueItem, Vaccination, VaccineMaster, VetVisit,
} from '../types';

/**
 * Resource path mapping notes: paths are kebab-case of the backend v2 table names,
 * taken directly from docs/openapi-v2.json (ground truth for the live backend).
 * If the backend team lands on different final paths, this is the single file to
 * update — no page/component references a raw URL string directly.
 */

function buildQuery(params?: ListQueryParams): string {
  if (!params) return '';
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') usp.set(key, String(value));
  });
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

/** Generic REST resource client factory: list/get/create/update/remove. */
function createResource<T, TCreate = Partial<T>, TUpdate = Partial<T>>(path: string) {
  return {
    async list(params?: ListQueryParams): Promise<Paginated<T>> {
      const { data } = await apiClient.get<Paginated<T>>(`${path}${buildQuery(params)}`);
      return data;
    },
    async get(id: string): Promise<T> {
      const { data } = await apiClient.get<T>(`${path}/${id}`);
      return data;
    },
    async create(payload: TCreate): Promise<T> {
      const { data } = await apiClient.post<T>(path, payload);
      return data;
    },
    async update(id: string, payload: TUpdate): Promise<T> {
      const { data } = await apiClient.put<T>(`${path}/${id}`, payload);
      return data;
    },
    async remove(id: string): Promise<void> {
      await apiClient.delete(`${path}/${id}`);
    },
  };
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const authApi = {
  async login(payload: LoginRequest): Promise<TokenResponse> {
    const { data } = await apiClient.post<TokenResponse>('/auth/login', payload);
    return data;
  },
  async me(): Promise<User> {
    const { data } = await apiClient.get<User>('/users/me/profile');
    return data;
  },
  async logout(): Promise<void> {
    await apiClient.post('/auth/logout');
  },
  async refresh(refreshToken: string): Promise<{ access_token: string; token_type: string }> {
    const { data } = await apiClient.post('/auth/refresh', { refresh_token: refreshToken });
    return data;
  },
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await apiClient.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword });
  },
};

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------
// v2 has no /reports/* chart endpoints yet — dashboard is KPI-tiles-only this
// round. A later backend round can add report endpoints for charts.

export const dashboardApi = {
  async kpis(): Promise<DashboardKPIs> {
    const { data } = await apiClient.get<DashboardKPIs>('/dashboard/kpis');
    return data;
  },
  async milkChart(startDate?: string, endDate?: string): Promise<MilkChartResponse> {
    const { data } = await apiClient.get<MilkChartResponse>('/dashboard/milk-chart', {
      params: { start_date: startDate, end_date: endDate },
    });
    return data;
  },
};

// The farm workbook's sheets, as read-only views over live data.
export const cattleRegisterApi = {
  async dashboard(): Promise<RegisterDashboard> {
    return (await apiClient.get<RegisterDashboard>('/cattle-register/dashboard')).data;
  },
  async animals(includeDeparted = false): Promise<RegisterSheet<RegisterAnimalRow>> {
    return (await apiClient.get<RegisterSheet<RegisterAnimalRow>>('/cattle-register/animals', { params: { include_departed: includeDeparted } })).data;
  },
  async breeding(): Promise<RegisterSheet<RegisterBreedingRow>> {
    return (await apiClient.get<RegisterSheet<RegisterBreedingRow>>('/cattle-register/breeding')).data;
  },
  async calvings(): Promise<RegisterSheet<RegisterCalvingRow>> {
    return (await apiClient.get<RegisterSheet<RegisterCalvingRow>>('/cattle-register/calvings')).data;
  },
  async milk(startDate?: string, endDate?: string): Promise<RegisterMilkSheet> {
    return (await apiClient.get<RegisterMilkSheet>('/cattle-register/milk', { params: { start_date: startDate, end_date: endDate } })).data;
  },
  async vetVisits(): Promise<RegisterSheet<RegisterVetRow>> {
    return (await apiClient.get<RegisterSheet<RegisterVetRow>>('/cattle-register/vet-visits')).data;
  },
  async settings(): Promise<RegisterSettings> {
    return (await apiClient.get<RegisterSettings>('/cattle-register/settings')).data;
  },
};

export const reportsApi = {
  async milkProduction(params: { startDate?: string; endDate?: string; animalId?: string }): Promise<MilkProductionReport> {
    const { data } = await apiClient.get<MilkProductionReport>('/reports/milk-production', {
      params: { start_date: params.startDate, end_date: params.endDate, animal_id: params.animalId, page_size: 1 },
    });
    return data;
  },
  async milkSalesByCustomer(params: { startDate?: string; endDate?: string; customerId?: string; month?: string; groups?: string[] }): Promise<CustomerMilkSalesReport> {
    // groups repeats as ?groups=a&groups=b (what FastAPI reads), not axios's default groups[]=.
    const qs = new URLSearchParams({ page_size: '1' });
    if (params.startDate) qs.set('start_date', params.startDate);
    if (params.endDate) qs.set('end_date', params.endDate);
    if (params.customerId) qs.set('customer_id', params.customerId);
    if (params.month) qs.set('month', params.month);
    (params.groups ?? []).forEach((g) => qs.append('groups', g));
    const { data } = await apiClient.get<CustomerMilkSalesReport>(`/reports/milk-sales-by-customer?${qs.toString()}`);
    return data;
  },
};

// ---------------------------------------------------------------------------
// Farm master / sheds / locations
// ---------------------------------------------------------------------------

export const farmMasterApi = createResource<FarmMaster>('/farm-master');
export const farmContactsApi = createResource<FarmContact>('/farm-contacts');
export const shedMasterApi = createResource<ShedMaster>('/shed-master');
export const locationsApi = createResource<Location>('/locations');

// ---------------------------------------------------------------------------
// Master data catalogues
// ---------------------------------------------------------------------------

export const assetTypesApi = createResource<AssetType>('/asset-types');
export const animalTypesApi = createResource<AnimalType>('/animal-types');
export const breedsApi = createResource<Breed>('/breeds');
export const feedItemsApi = createResource<FeedItem>('/feed-items');
export const medicineMasterApi = createResource<MedicineMaster>('/medicine-master');
export const vaccineMasterApi = createResource<VaccineMaster>('/vaccine-master');
export const expenseCategoriesApi = createResource<ExpenseCategory>('/expense-categories');
export const suppliersApi = createResource<Supplier>('/suppliers');
const customersBase = createResource<Customer>('/customers');
export const customersApi = {
  ...customersBase,
  /** Admin-issued PIN (Delivery app, Phase 2) - a separate call, not a
   * plain Customer field, since the hash never round-trips back out
   * (see CustomerRead.has_pin on the backend). */
  async setPin(customerId: string, pin: string): Promise<void> {
    await apiClient.put(`/customers/${customerId}/pin`, { pin });
  },
};
export const customerPaymentsApi = createResource<CustomerPayment>('/customer-payments');
export const customerAccountsApi = {
  async list(params?: { billing_mode?: BillingMode; status?: AccountStatus; q?: string }): Promise<CustomerAccountSummary[]> {
    const { data } = await apiClient.get<CustomerAccountSummary[]>('/customer-accounts', { params });
    return data;
  },
};

// ---------------------------------------------------------------------------
// Assets
// ---------------------------------------------------------------------------

const assetsBase = createResource<Asset, AssetCreate, Partial<Asset>>('/assets');
export const assetsApi = {
  ...assetsBase,
  async byQr(qrCode: string): Promise<Asset> {
    const { data } = await apiClient.get<Asset>(`/assets/qr/${encodeURIComponent(qrCode)}`);
    return data;
  },
  async regenerateQr(assetId: string): Promise<Asset> {
    const { data } = await apiClient.post<Asset>(`/assets/${assetId}/qr`);
    return data;
  },
};

// ---------------------------------------------------------------------------
// Animals
// ---------------------------------------------------------------------------

const animalsBase = createResource<Animal, AnimalCreate, AnimalUpdate>('/animals');
export const animalsApi = {
  ...animalsBase,
  /** The QR-scan convenience endpoint (WF-02) — resolves straight to the Animal profile. */
  async byQr(qrCode: string): Promise<Animal> {
    const { data } = await apiClient.get<Animal>(`/animals/qr/${encodeURIComponent(qrCode)}`);
    return data;
  },
  /** Combined lifecycle history (Animal_Events) for the Profile → Events tab. */
  async history(animalId: string): Promise<AnimalEvent[]> {
    const { data } = await apiClient.get<Paginated<AnimalEvent>>(`/animals/${animalId}/history${buildQuery({ page_size: 100 })}`);
    return data.items;
  },
  // Matches on animal_id (business code) via the backend's `q` filter.
  async findByCode(code: string): Promise<Animal> {
    const page = await apiClient.get<Paginated<Animal>>(`/animals${buildQuery({ q: code, page_size: 1 })}`);
    const match = page.data.items[0];
    if (!match) throw Object.assign(new Error('Animal not found'), { response: { status: 404 } });
    return match;
  },
  /** Downloads the fill-in-the-blanks CSV template for bulk import. */
  async downloadBulkImportTemplate(): Promise<Blob> {
    const { data } = await apiClient.get('/animals/bulk-import/template', { responseType: 'blob' });
    return data;
  },
  /** Full-detail CSV of every field the compact list table can't fit —
   * location, purchase cost, QR code, timestamps, etc. Same filters as the
   * list view, so "export what I'm looking at" works as expected. */
  async exportCsv(params: ListQueryParams): Promise<Blob> {
    const { data } = await apiClient.get(`/animals/export${buildQuery(params)}`, { responseType: 'blob' });
    return data;
  },
  /** Uploads a filled-in CSV; each row is validated/created independently
   * server-side, so a summary with per-row errors comes back either way.
   * photosZip is optional — matches the CSV's photo_filenames column to
   * files inside it, since a CSV cell can't hold an image. */
  async bulkImport(file: File, photosZip?: File | null): Promise<BulkImportResult> {
    const form = new FormData();
    form.append('file', file);
    if (photosZip) form.append('photos_zip', photosZip);
    const { data } = await apiClient.post<BulkImportResult>('/animals/bulk-import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },
  /** Column-by-column explanation of the bulk import CSV, for hover help in the modal. */
  async bulkImportColumns(): Promise<BulkImportColumnHelp[]> {
    const { data } = await apiClient.get<BulkImportColumnHelp[]>('/animals/bulk-import/columns');
    return data;
  },
  /** One aggregated view of an animal's whole life — family, lifetime milk/
   * lactation figures, a monthly production trend, growth history, and a
   * health summary — for the Animal Lifecycle page. */
  async lifecycle(animalId: string): Promise<AnimalLifecycle> {
    const { data } = await apiClient.get<AnimalLifecycle>(`/animals/${animalId}/lifecycle`);
    return data;
  },
};

export const animalIdentificationApi = createResource<AnimalIdentification>('/animal-identification');
export const animalEventsApi = createResource<AnimalEvent>('/animal-events');
export const animalMovementsApi = createResource<AnimalMovement>('/animal-movements');
export const animalOffspringLinksApi = createResource<AnimalOffspringLink>('/animal-offspring-links');
export const animalPurchasesApi = createResource<AnimalPurchase>('/animal-purchases');

export const attachmentsApi = {
  ...createResource<Attachment>('/attachments'),
  async upload(file: File, entityType: string, entityId: string): Promise<Attachment> {
    const form = new FormData();
    form.append('entity_type', entityType);
    form.append('entity_id', entityId);
    form.append('file', file);
    const { data } = await apiClient.post<Attachment>('/attachments', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },
  /** The actual file bytes, as an authenticated blob — see
   * ILMSAttachmentPreview.tsx for why this can't just be a plain <img src>. */
  async fetchFileBlob(attachmentId: string): Promise<Blob> {
    const { data } = await apiClient.get<Blob>(`/attachments/${attachmentId}/file`, { responseType: 'blob' });
    return data;
  },
};

// ---------------------------------------------------------------------------
// Dairy
// ---------------------------------------------------------------------------

export const cowProfilesApi = createResource<CowProfile>('/cow-profiles');
export const calfRecordsApi = createResource<CalfRecord>('/calf-records');
export const lactationsApi = createResource<Lactation>('/lactations');
const milkSessionsBase = createResource<MilkSession>('/milk-sessions');
export const milkSessionsApi = {
  ...milkSessionsBase,
  async updateApproval(sessionId: string, status: MilkCollectionApprovalStatus, reviewNotes?: string): Promise<MilkSession> {
    const { data } = await apiClient.put<MilkSession>(`/milk-sessions/${sessionId}/approval`, { approval_status: status, review_notes: reviewNotes });
    return data;
  },
  /** Attach the photo to a just-created entry - Create permission is enough. */
  async setPhoto(sessionId: string, photoAttachmentId: string): Promise<MilkSession> {
    const { data } = await apiClient.put<MilkSession>(`/milk-sessions/${sessionId}/photo`, { photo_attachment_id: photoAttachmentId });
    return data;
  },
  /** Every cow's milk per shift added up - newest first, last 14 days by default. */
  async shifts(params?: { date_from?: string; date_to?: string }): Promise<MilkShift[]> {
    const { data } = await apiClient.get<MilkShift[]>(`/milk-sessions/shifts${buildQuery(params)}`);
    return data;
  },
  async approveShift(productionDate: string, sessionType: MilkSessionType, status: MilkCollectionApprovalStatus): Promise<MilkShift> {
    const { data } = await apiClient.post<MilkShift>('/milk-sessions/shifts/approve', {
      production_date: productionDate, session_type: sessionType, approval_status: status,
    });
    return data;
  },
};
export const milkDailySummaryApi = {
  async list(params?: ListQueryParams): Promise<Paginated<MilkDailySummary>> {
    const { data } = await apiClient.get<Paginated<MilkDailySummary>>(`/milk-daily-summary${buildQuery(params)}`);
    return data;
  },
};
export const dairyCollectionHubsApi = createResource<DairyCollectionHub>('/dairy-collection-hubs');
export const subscriptionPlansApi = createResource<SubscriptionPlan>('/subscription-plans');

/** Upcoming day changes (pause / move AM-PM / litres). The server keeps one
 * per customer per date, so saving the same date again replaces it -
 * "edit" is just another save; delete puts that day back to the plan. */
function dayChangeBody(p: Partial<SubscriptionDayChange>) {
  const num = (v: unknown) => (v === '' || v == null ? undefined : Number(v));
  return {
    customer_id: p.customer_id, kind: p.kind, start_date: p.start_date,
    end_date: p.kind === 'pause' && p.end_date ? p.end_date : undefined,
    to_shift: p.kind === 'move' ? p.to_shift : undefined,
    morning_litres: p.kind === 'quantity' ? num(p.morning_litres) : undefined,
    evening_litres: p.kind === 'quantity' ? num(p.evening_litres) : undefined,
    note: p.note || undefined,
  };
}
export const subscriptionDayChangesApi = {
  async list(params?: ListQueryParams): Promise<Paginated<SubscriptionDayChange>> {
    const { data } = await apiClient.get<SubscriptionDayChange[]>('/subscription-day-changes');
    const q = (params?.search ?? '').toString().toLowerCase();
    const all = q ? data.filter((r) => r.customer_name.toLowerCase().includes(q) || r.summary.toLowerCase().includes(q)) : data;
    const page = params?.page ?? 1;
    const pageSize = params?.page_size ?? 20;
    return { items: all.slice((page - 1) * pageSize, page * pageSize), total: all.length, page, page_size: pageSize };
  },
  async create(payload: Partial<SubscriptionDayChange>): Promise<SubscriptionDayChange> {
    const { data } = await apiClient.post<SubscriptionDayChange[]>('/subscription-day-changes', dayChangeBody(payload));
    return data[0];
  },
  async update(_id: string, payload: Partial<SubscriptionDayChange>): Promise<SubscriptionDayChange> {
    return subscriptionDayChangesApi.create(payload);
  },
  async remove(id: string): Promise<void> {
    await apiClient.delete(`/subscription-day-changes/${id}`);
  },
};
const milkSalesBase = createResource<MilkSale>('/milk-sales');
export const milkSalesApi = {
  ...milkSalesBase,
  async updateApproval(saleId: string, status: MilkCollectionApprovalStatus, reviewNotes?: string): Promise<MilkSale> {
    const { data } = await apiClient.put<MilkSale>(`/milk-sales/${saleId}/approval`, { approval_status: status, review_notes: reviewNotes });
    return data;
  },
  /** Attach the Hub slip photo to a just-created sale - Create permission is enough. */
  async setPhoto(saleId: string, photoAttachmentId: string): Promise<MilkSale> {
    const { data } = await apiClient.put<MilkSale>(`/milk-sales/${saleId}/photo`, { photo_attachment_id: photoAttachmentId });
    return data;
  },
  /** Still-unresolved (customer, day) pairs from active subscriptions —
   * the Yes/No prompts shown on the Milk Sales tab. */
  async pendingConfirmations(): Promise<PendingSubscriptionConfirmation[]> {
    const { data } = await apiClient.get<PendingSubscriptionConfirmation[]>('/milk-sales/pending-confirmations');
    return data;
  },
  /** Confirms or declines one subscription day — Yes creates one
   * already-Approved Milk Sale (two, for a Both-shift subscription — one
   * per shift); No creates nothing. Either way the day is marked resolved
   * and won't be asked again. */
  async confirm(payload: MilkSaleConfirmRequest): Promise<MilkSale[]> {
    const { data } = await apiClient.post<MilkSale[]>('/milk-sales/confirm', payload);
    return data;
  },
};

// ---------------------------------------------------------------------------
// Breeding
// ---------------------------------------------------------------------------

export const heatDetectionsApi = createResource<HeatDetection>('/heat-detections');
export const breedingRecordsApi = {
  ...createResource<BreedingRecord>('/breeding-records'),
  async alerts(): Promise<BreedingAlerts> {
    const { data } = await apiClient.get<BreedingAlerts>('/breeding-records/alerts');
    return data;
  },
};
export const pregnancyRecordsApi = createResource<PregnancyRecord>('/pregnancy-records');
export const calvingRecordsApi = createResource<CalvingRecord>('/calving-records');
export const lambingKiddingRecordsApi = createResource<LambingKiddingRecord>('/lambing-kidding-records');
export const semenMasterApi = createResource<SemenMaster>('/semen-master');
export const semenInventoryApi = createResource<SemenInventory>('/semen-inventory');

export const breedingKpisApi = {
  ...createResource<BreedingKPI>('/breeding-kpis'),
  async recompute(animalId: string): Promise<BreedingKPI> {
    const { data } = await apiClient.post<BreedingKPI>(`/breeding-kpis/${animalId}/recompute`);
    return data;
  },
};

// ---------------------------------------------------------------------------
// Health
// ---------------------------------------------------------------------------

export const healthRecordsApi = createResource<HealthRecord>('/health-records');
export const vetVisitsApi = createResource<VetVisit>('/vet-visits');
export const treatmentsApi = createResource<Treatment>('/treatments');
export const treatmentMedicinesApi = createResource<TreatmentMedicine>('/treatment-medicines');
export const vaccinationsApi = createResource<Vaccination>('/vaccinations');
export const dewormingApi = createResource<Deworming>('/deworming');
export const diseaseOutbreaksApi = createResource<DiseaseOutbreak>('/disease-outbreaks');
export const growthRecordsApi = createResource<GrowthRecord>('/growth-records');

export const vaccinationSchedulesApi = {
  ...createResource<VaccinationSchedule>('/vaccination-schedules'),
  async due(animalId: string): Promise<VaccinationDueItem[]> {
    const { data } = await apiClient.get<VaccinationDueItem[]>(`/vaccination-schedules/due${buildQuery({ animal_id: animalId })}`);
    return data;
  },
};

// ---------------------------------------------------------------------------
// Poultry
// ---------------------------------------------------------------------------

const poultryBatchesBase = createResource<PoultryBatch>('/poultry-batches');
export const poultryBatchesApi = {
  ...poultryBatchesBase,
  async addBirds(batchId: string, count: number): Promise<PoultryBatch> {
    const { data } = await apiClient.post<PoultryBatch>(`/poultry-batches/${batchId}/additions`, { count });
    return data;
  },
  async reconciliation(batchId: string): Promise<PoultryBatchReconciliation> {
    const { data } = await apiClient.get<PoultryBatchReconciliation>(`/poultry-batches/${batchId}/reconciliation`);
    return data;
  },
};
export const poultryMortalityApi = createResource<PoultryMortality>('/poultry-mortality');
export const eggProductionApi = createResource<EggProduction>('/egg-production');
export const poultryFeedRecordsApi = createResource<PoultryFeedRecord>('/poultry-feed-records');
export const poultryKpisApi = {
  ...createResource<PoultryKPI>('/poultry-kpis'),
  async recompute(batchId: string, asOfDate: string, weightGainKg?: number): Promise<PoultryKPI> {
    const { data } = await apiClient.post<PoultryKPI>(`/poultry-kpis/${batchId}/recompute`, {
      as_of_date: asOfDate,
      weight_gain_kg: weightGainKg,
    });
    return data;
  },
};

// ---------------------------------------------------------------------------
// Coconut
// ---------------------------------------------------------------------------

const coconutTreesBase = createResource<CoconutTree, CoconutTreeCreate, Partial<CoconutTree>>('/coconut-trees');
export const coconutTreesApi = {
  ...coconutTreesBase,
  // farm_id is a query param on create (not part of the request body) per openapi-v2.json.
  async create(payload: CoconutTreeCreate, farmId: string): Promise<CoconutTree> {
    const { data } = await apiClient.post<CoconutTree>(`/coconut-trees${buildQuery({ farm_id: farmId })}`, payload);
    return data;
  },
  async downloadBulkImportTemplate(): Promise<Blob> {
    const { data } = await apiClient.get('/coconut-trees/bulk-import/template', { responseType: 'blob' });
    return data;
  },
  async bulkImportColumns(): Promise<BulkImportColumnHelp[]> {
    const { data } = await apiClient.get<BulkImportColumnHelp[]>('/coconut-trees/bulk-import/columns');
    return data;
  },
  async bulkImport(file: File): Promise<CoconutBulkImportResult> {
    const form = new FormData();
    form.append('file', file);
    const { data } = await apiClient.post<CoconutBulkImportResult>('/coconut-trees/bulk-import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },
  /** Distinct tree_group values in use, with how many trees are in each — backs the group-harvest picker. */
  async groups(): Promise<{ tree_group: string; tree_count: number }[]> {
    const { data } = await apiClient.get<{ tree_group: string; tree_count: number }[]>('/coconut-trees/groups');
    return data;
  },
};
export const coconutHarvestsApi = {
  ...createResource<CoconutHarvest>('/coconut-harvests'),
  async yieldReport(year: number, splitGroupHarvests = false): Promise<CoconutYieldReport> {
    const { data } = await apiClient.get<CoconutYieldReport>(
      `/coconut-harvests/yield-report${buildQuery({ year, split_group_harvests: splitGroupHarvests })}`
    );
    return data;
  },
};

// ---------------------------------------------------------------------------
// Inventory
// ---------------------------------------------------------------------------

export const inventoryItemsApi = createResource<InventoryItem>('/inventory-items');
export const inventoryLocationsApi = createResource<InventoryLocation>('/inventory-locations');
const inventoryLotsBase = createResource<InventoryLot>('/inventory-lots');
export const inventoryLotsApi = {
  ...inventoryLotsBase,
  async issue(
    lotId: string,
    payload: { quantity: number; transaction_type?: InventoryTransactionType; reference_type?: string; reference_id?: string; reason?: string }
  ): Promise<InventoryTransaction> {
    const { data } = await apiClient.post<InventoryTransaction>(`/inventory-lots/${lotId}/issue`, payload);
    return data;
  },
};
export const inventoryTransactionsApi = {
  async list(params?: ListQueryParams): Promise<Paginated<InventoryTransaction>> {
    const { data } = await apiClient.get<Paginated<InventoryTransaction>>(`/inventory-transactions${buildQuery(params)}`);
    return data;
  },
};
export const feedConsumptionApi = createResource<FeedConsumption>('/feed-consumption');

// ---------------------------------------------------------------------------
// Procurement & Sales
// ---------------------------------------------------------------------------

const purchaseOrdersBase = createResource<PurchaseOrder, PurchaseOrderCreate, Partial<PurchaseOrderCreate>>('/purchase-orders');
export const purchaseOrdersApi = {
  ...purchaseOrdersBase,
  async updateStatus(poId: string, status: PurchaseOrderStatus): Promise<PurchaseOrder> {
    const { data } = await apiClient.put<PurchaseOrder>(`/purchase-orders/${poId}/status`, { status });
    return data;
  },
};

const purchasesBase = createResource<Purchase, PurchaseCreate, Partial<PurchaseCreate>>('/purchases');
export const purchasesApi = {
  ...purchasesBase,
  async updatePaymentStatus(purchaseId: string, status: PaymentStatus): Promise<Purchase> {
    const { data } = await apiClient.put<Purchase>(`/purchases/${purchaseId}/payment-status`, { payment_status: status });
    return data;
  },
};

const salesBase = createResource<Sale, SaleCreate, Partial<SaleCreate>>('/sales');
export const salesApi = {
  ...salesBase,
  async updatePaymentStatus(saleId: string, status: PaymentStatus): Promise<Sale> {
    const { data } = await apiClient.put<Sale>(`/sales/${saleId}/payment-status`, { payment_status: status });
    return data;
  },
};

// ---------------------------------------------------------------------------
// Finance
// ---------------------------------------------------------------------------

const expensesBase = createResource<Expense>('/expenses');
export const expensesApi = {
  ...expensesBase,
  async updateApproval(expenseId: string, status: ExpenseApprovalStatus): Promise<Expense> {
    const { data } = await apiClient.put<Expense>(`/expenses/${expenseId}/approval`, { approval_status: status });
    return data;
  },
};

// ---------------------------------------------------------------------------
// Operations (Tasks)
// ---------------------------------------------------------------------------

export const tasksApi = createResource<Task>('/tasks');

/** Recurring work's Daily Log - each person's job per day and shift. */
export const taskOccurrencesApi = {
  async list(params: { from_date: string; to_date: string; assigned_to?: string; state?: string }): Promise<TaskOccurrence[]> {
    const { data } = await apiClient.get<TaskOccurrence[]>('/task-occurrences', { params });
    return data;
  },
  async reopen(id: string): Promise<TaskOccurrence> {
    const { data } = await apiClient.post<TaskOccurrence>(`/task-occurrences/${id}/reopen`);
    return data;
  },
};

const taskAssignmentsBase = createResource<TaskAssignment>('/task-assignments');
export const taskAssignmentsApi = {
  ...taskAssignmentsBase,
  async start(assignmentId: string): Promise<TaskAssignment> {
    const { data } = await apiClient.post<TaskAssignment>(`/task-assignments/${assignmentId}/start`);
    return data;
  },
  async complete(assignmentId: string, remarks?: string): Promise<TaskAssignment> {
    const { data } = await apiClient.post<TaskAssignment>(`/task-assignments/${assignmentId}/complete`, { remarks });
    return data;
  },
  async verify(assignmentId: string): Promise<TaskAssignment> {
    const { data } = await apiClient.post<TaskAssignment>(`/task-assignments/${assignmentId}/verify`);
    return data;
  },
};

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

export const utilityMetersApi = createResource<UtilityMeter>('/utility-meters');
export const utilityReadingsApi = createResource<UtilityReading>('/utility-readings');

// ---------------------------------------------------------------------------
// Security / RBAC / Admin
// ---------------------------------------------------------------------------

const usersBase = createResource<User>('/users');
export const usersApi = {
  ...usersBase,
  async updateModuleAccess(userId: string, modules: string[]): Promise<{ modules: string[] }> {
    const { data } = await apiClient.put<{ modules: string[] }>(`/users/${userId}/module-access`, { modules });
    return data;
  },
  async resetPassword(userId: string, newPassword: string): Promise<void> {
    await apiClient.put(`/users/${userId}/password`, { new_password: newPassword });
  },
};

const rolesBase = createResource<Role>('/roles');
export const rolesApi = {
  ...rolesBase,
  async permissions(roleId: string): Promise<PermissionRead[]> {
    const { data } = await apiClient.get<PermissionRead[]>(`/roles/${roleId}/permissions`);
    return data;
  },
  async setPermissions(roleId: string, permissionIds: string[]): Promise<void> {
    await apiClient.put(`/roles/${roleId}/permissions`, { permission_ids: permissionIds });
  },
};

export const permissionsApi = {
  async list(module?: string): Promise<PermissionRead[]> {
    const { data } = await apiClient.get<PermissionRead[]>(`/permissions${buildQuery({ module })}`);
    return data;
  },
};

export const userRolesApi = createResource<UserRoleAssignment>('/user-roles');

export const userSessionsApi = {
  async list(params?: ListQueryParams): Promise<Paginated<UserSession>> {
    const { data } = await apiClient.get<Paginated<UserSession>>(`/user-sessions${buildQuery(params)}`);
    return data;
  },
  async revoke(sessionId: string): Promise<UserSession> {
    const { data } = await apiClient.post<UserSession>(`/user-sessions/${sessionId}/revoke`);
    return data;
  },
};

export const activityLogApi = {
  async list(params?: ListQueryParams): Promise<Paginated<ActivityLog>> {
    const { data } = await apiClient.get<Paginated<ActivityLog>>(`/activity-log${buildQuery(params)}`);
    return data;
  },
  async export(params?: ListQueryParams): Promise<void> {
    await apiClient.post(`/activity-log/export${buildQuery(params)}`);
  },
};

const notificationsBase = createResource<Notification>('/notifications');
export const notificationsApi = {
  ...notificationsBase,
  async markRead(id: string): Promise<Notification> {
    const { data } = await apiClient.post<Notification>(`/notifications/${id}/read`);
    return data;
  },
  async markAllRead(): Promise<void> {
    await apiClient.post('/notifications/read-all');
  },
  // No dedicated count endpoint — the paginated list's `total` gives the
  // unread count when filtered to status_filter=Unread.
  async unreadCount(): Promise<number> {
    const data = await notificationsBase.list({ status_filter: 'Unread', page_size: 1 } as ListQueryParams);
    return data.total;
  },
};

export const notificationRulesApi = createResource<NotificationRule>('/notification-rules');
export const systemSettingsApi = createResource<SystemSetting>('/system-settings');

// ---------------------------------------------------------------------------
// Public website enquiries (staff-facing view of the public site's contact
// form submissions — POST is unauthenticated and lives on the public site
// itself, not here)
// ---------------------------------------------------------------------------

export const enquiriesApi = createResource<PublicEnquiry>('/public/enquiries');
